import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { residencesService } from '../../../services';
import { ClientsSkeleton } from '../../../skeletons';
import { Button, PageHeader } from '../../../components';
import { ChevronDown, AlertCircle, Pencil, Search, MapPin, DoorOpen, Layers, LayoutGrid, ArrowRight, Plus } from 'lucide-react';
import { flagTypeStyle } from '../../../utils/flagTypes';

// Uploaded files are served from the API origin (strip trailing /api). A stored
// profile-pic URL is relative (/uploads/…), so prefix it before rendering.
const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const resolveImg = (url) => (url ? (url.startsWith('http') ? url : `${API_ORIGIN}${url}`) : null);

// Show first + last name only (drop any middle names).
const firstLast = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return name || 'Unknown';
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

// The API sends 'N/A' for a resident with no room recorded. The card renders
// its own em-dash for an empty value, so fold that sentinel back to empty.
const roomLabel = (room) => (!room || room === 'N/A' ? '' : room);

// Up to two initials for the avatar fallback.
const avatarInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

// Shared card shell — matches the dashboard cards.
const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

/**
 * Care Home Manager - Residences Page
 * List and manage residences (care home residents) in their organization
 */
const Residences = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  // Drive the card layout off the logged-in manager's organization type
  // (stored on the user) rather than each residence's own type, so a
  // domiciliary manager always sees the domiciliary view (address +
  // preferred name) and a care-home manager sees unit/floor + preferred name.
  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';
  // Domiciliary orgs refer to the people they care for as "clients"; care homes
  // call them "residents".
  const noun = isDomiciliary ? 'Client' : 'Resident';
  const nounPlural = isDomiciliary ? 'Clients' : 'Residents';
  const [residences, setResidences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Client-side search over the already-fetched list (view-only; no refetch).
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchResidences();
  }, []);

  const fetchResidences = async () => {
    try {
      const response = await residencesService.getAllResidences();
      if (response.success) {
        setResidences(response.data || []);
        setError('');
      } else {
        setError(response.message || 'Failed to load residences');
      }
    } catch (err) {
      console.error('Error fetching residences:', err);
      setError(err.message || 'Failed to load residences');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (e, id) => {
    e.stopPropagation();
    navigate(`/manager/residences/${id}/edit`);
  };

  if (loading) {
    return <ClientsSkeleton />;
  }

  // Filter the fetched list for display only — does not touch fetching/logic.
  const q = query.trim().toLowerCase();
  const visibleResidences = q
    ? residences.filter((r) => {
        const hay = `${r.name || ''} ${r.preferred_name || ''} ${r.residence_code || ''} ${r.address || ''} ${roomLabel(r.room_number)} ${r.unit || ''}`.toLowerCase();
        return hay.includes(q);
      })
    : residences;

  return (
    <div className="space-y-6 pb-8">
      {/* Header Section */}
      <PageHeader
        title={nounPlural}
        count={residences.length}
        subtitle={`All ${nounPlural.toLowerCase()} and their care details.`}
        actions={(
          <>
            {/* Search — filters the loaded list client-side */}
            <div className="relative">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${nounPlural.toLowerCase()}…`}
                className="h-10 w-full sm:w-[240px] pl-9 pr-3 rounded-xl bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-800 text-[13.5px] text-[#334155] dark:text-gray-200 placeholder-[#94A3B8] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition"
              />
            </div>

            {/* All Residents filter (unchanged behaviour) */}
            <Button variant="secondary" className="!justify-between min-w-[150px]" rightIcon={<ChevronDown className="w-4 h-4 text-[#94A3B8]" />}>
              All {nounPlural}
            </Button>

            {/* Add New - opens the 6-step profile form straight away. It used
                to go to the Enquiry List; enquiries now live only under
                Enquiries & Assessments. */}
            <Button variant="primary" onClick={() => navigate('/manager/residences/new')} leftIcon={<Plus className="w-4 h-4" />}>
              Add {noun}
            </Button>
          </>
        )}
      />

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 dark:text-red-400 font-medium">Error</p>
            <p className="text-red-600 dark:text-red-500 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Card grid */}
      {residences.length === 0 ? (
        <div className="py-14 text-center bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800">
          <p className="text-gray-500 dark:text-gray-400 font-medium">No {nounPlural.toLowerCase()} found.</p>
        </div>
      ) : visibleResidences.length === 0 ? (
        <div className="py-14 text-center bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800">
          <p className="text-gray-500 dark:text-gray-400 font-medium">No {nounPlural.toLowerCase()} match &ldquo;{query}&rdquo;.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
          {/* Four across only on genuinely wide screens — at laptop widths four
              cards left the Room / Unit / Floor tile too narrow to read. */}
          {visibleResidences.map((res, index) => {
            const displayId = res.residence_code || `#${(res.id || 550987).toString().padStart(6, '0')}`;
            const preferredName = res.name ? res.name.split(' ')[0] : 'Unknown';
            const activeFlags = Array.isArray(res.care_flags)
              ? res.care_flags.filter((f) => f.is_active)
              : [];

            return (
              <div
                key={res.id || index}
                onClick={() => navigate(`/manager/residences/${res.id}`)}
                className={`group relative overflow-hidden ${CARD} p-5 flex flex-col cursor-pointer transition duration-200 hover:-translate-y-1 hover:shadow-[0_16px_38px_-12px_rgba(37,99,235,0.18)] hover:border-blue-300`}
                style={{ borderTopColor: '#2563EB', borderTopWidth: '3px' }}
              >

                {/* Top Row: ID + flags + edit */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-bold text-[#64748B] dark:text-gray-400 bg-[#F4F7FC] dark:bg-[#31373f] border border-[#EEF2F8] dark:border-gray-700 px-2 py-[3px] rounded-[7px] tabular-nums">
                    {displayId}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {activeFlags.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap justify-end">
                        {activeFlags.slice(0, 3).map((f) => {
                          const st = flagTypeStyle(f.flag_type, isDark);
                          return (
                            <span
                              key={f.id}
                              title={f.specific_detail || f.flag_type}
                              className="px-[7px] py-[3px] rounded-md text-[9.5px] font-extrabold whitespace-nowrap"
                              style={{ background: st.bg, color: st.color }}
                            >
                              {st.short}
                            </span>
                          );
                        })}
                        {activeFlags.length > 3 && (
                          <span className="px-[7px] py-[3px] rounded-md text-[9.5px] font-extrabold bg-[#F4F7FC] dark:bg-gray-700 text-[#64748B] dark:text-gray-400">
                            +{activeFlags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={(e) => handleEdit(e, res.id)}
                      className="w-7 h-7 grid place-items-center rounded-lg border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] text-[#94A3B8] opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition hover:text-blue-600 hover:bg-[#EEF3FF] dark:hover:bg-blue-900/20 hover:border-[#D8E4FF]"
                      title={`Edit ${noun.toLowerCase()}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#EEF2F8] dark:border-gray-800 my-[14px]" />

                {/* Profile */}
                <div className="flex items-center gap-[13px]">
                  {res.image_url ? (
                    <img
                      src={resolveImg(res.image_url)}
                      alt={res.name}
                      className="w-[50px] h-[50px] rounded-[15px] object-cover shrink-0 ring-1 ring-inset ring-[#D8E4FF]"
                    />
                  ) : (
                    <span className="w-[50px] h-[50px] rounded-[15px] shrink-0 grid place-items-center text-[16px] font-extrabold text-blue-600 bg-gradient-to-br from-[#EEF3FF] to-white dark:from-[#31373f] dark:to-[#262b33] ring-1 ring-inset ring-[#D8E4FF] dark:ring-gray-700">
                      {avatarInitials(res.name)}
                    </span>
                  )}
                  <div className="min-w-0">
                    <span className="block text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">{noun}</span>
                    <span className="block text-[16px] font-bold text-[#0B1220] dark:text-gray-100 tracking-tight leading-tight mt-px truncate">{firstLast(res.name)}</span>
                    <span className="text-[11.5px] text-[#64748B] dark:text-gray-400">Prefers &ldquo;{res.preferred_name || preferredName}&rdquo;</span>
                  </div>
                </div>

                {/* Detail tile — domiciliary: address · care-home: unit + floor */}
                {isDomiciliary ? (
                  <div className="flex items-start gap-2.5 mt-4 p-3 bg-[#F4F7FC] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800 rounded-xl">
                    <span className="w-[30px] h-[30px] rounded-[9px] bg-white dark:bg-[#262b33] border border-[#EEF2F8] dark:border-gray-800 grid place-items-center text-blue-600 shrink-0">
                      <MapPin className="w-[15px] h-[15px]" />
                    </span>
                    <div className="min-w-0">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">Address</span>
                      <span className="block text-[12.5px] font-semibold text-[#334155] dark:text-gray-300 leading-snug line-clamp-2" title={res.address || ''}>{res.address || '—'}</span>
                    </div>
                  </div>
                ) : (
                  // Label row (small icon + word) above the value, so the value
                  // gets the cell's full width. The old side-by-side 30px icon
                  // boxes left ~40px for the text, and "Bluebell" became "B..".
                  // Values wrap to two lines rather than being cut off.
                  <div className={`grid ${res.unit ? 'grid-cols-3' : 'grid-cols-2'} mt-4 bg-[#F4F7FC] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800 rounded-xl divide-x divide-[#E3E9F3] dark:divide-gray-800`}>
                    {[
                      { key: 'room', Icon: DoorOpen, label: 'Room', value: roomLabel(res.room_number) },
                      // Unit is optional — a home that doesn't use units
                      // shouldn't carry a column of em-dashes on every card.
                      ...(res.unit ? [{ key: 'unit', Icon: LayoutGrid, label: 'Unit', value: res.unit }] : []),
                      { key: 'floor', Icon: Layers, label: 'Floor', value: res.floor_number },
                    ].map(({ key, Icon, label, value }) => (
                      <div key={key} className="min-w-0 px-3 py-2.5">
                        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">
                          <Icon className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          {label}
                        </span>
                        <span
                          className="block mt-1 text-[13px] font-semibold text-[#334155] dark:text-gray-300 leading-snug break-words line-clamp-2"
                          title={value || ''}
                        >
                          {value || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Address — shown for care-home residents too (domiciliary
                    already shows it in the detail tile above). */}
                {!isDomiciliary && (
                  <div className="flex items-start gap-2.5 mt-3 p-3 bg-[#F4F7FC] dark:bg-[#20242c] border border-[#EEF2F8] dark:border-gray-800 rounded-xl">
                    <span className="w-[30px] h-[30px] rounded-[9px] bg-white dark:bg-[#262b33] border border-[#EEF2F8] dark:border-gray-800 grid place-items-center text-blue-600 shrink-0">
                      <MapPin className="w-[15px] h-[15px]" />
                    </span>
                    <div className="min-w-0">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">Address</span>
                      <span className="block text-[12.5px] font-semibold text-[#334155] dark:text-gray-300 leading-snug line-clamp-2" title={res.address || ''}>{res.address || '—'}</span>
                    </div>
                  </div>
                )}

                {/* Footer — real flag summary + view affordance */}
                <div className="flex items-center justify-between mt-[14px] pt-3 border-t border-dashed border-[#E3E9F3] dark:border-gray-800">
                  {activeFlags.length > 0 ? (
                    <span className="inline-flex items-center gap-[7px] text-[11.5px] font-semibold text-[#64748B] dark:text-gray-400">
                      <span className="w-[7px] h-[7px] rounded-full bg-rose-500 shadow-[0_0_0_3px_rgba(244,63,94,0.14)]" />
                      {activeFlags.length} active {activeFlags.length === 1 ? 'flag' : 'flags'}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-[7px] text-[11.5px] font-semibold text-[#64748B] dark:text-gray-400">
                      <span className="w-[7px] h-[7px] rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.14)]" />
                      No active flags
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-blue-600">
                    View profile
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Residences;
